// stores/useStockSymbolsStore.ts
import { defineStore } from "pinia";
import axios from "axios";
import { ref, shallowRef, computed, nextTick, watch } from "vue";
import localforage from "localforage";
import { useRuntimeConfig } from "#app";

import type { MostUpToDateDataType } from "../utils/types";

/**
 * Interface representing a stock item with its properties
 * @interface StockItem
 * @property {string} SYMBOL - The stock symbol/ticker
 * @property {string} NAME - The company name
 * @property {number} PC_CHG - The percentage change in price
 * @property {number} NET_CHG - The net change in price
 * @property {number} BID - The current bid price
 * @property {any} [key: string] - Additional dynamic properties
 */
export interface StockItem {
	SYMBOL: string;
	NAME: string;
	PC_CHG: number;
	NET_CHG: number;
	BID: number;
	[key: string]: any;
}

type ReferencedStocksInfo = {
	[key: string]: MostUpToDateDataType;
};

// Define the localforage instance outside the store definition
// so it's created only once when the module is imported.
const stockCache = localforage.createInstance({
	name: "stockDB",
	storeName: "indexSymbols",
	driver: localforage.INDEXEDDB // Ensure INDEXEDDB is available, or provide fallbacks
});

// Cached songbird config (shared across all store instances, fetched once)
let _songbirdConfig: { apiUrl: string; apiKey: string } | null = null;
let _songbirdConfigPromise: Promise<{ apiUrl: string; apiKey: string }> | null =
	null;

async function getSongbirdConfig(): Promise<{
	apiUrl: string;
	apiKey: string;
}> {
	if (_songbirdConfig) return _songbirdConfig;
	if (_songbirdConfigPromise) return _songbirdConfigPromise;

	_songbirdConfigPromise = axios
		.get("/api/songbird-config")
		.then((res) => {
			_songbirdConfig = res.data;
			return _songbirdConfig!;
		})
		.catch((err) => {
			_songbirdConfigPromise = null;
			throw err;
		});

	return _songbirdConfigPromise;
}

// Define the store using the setup function syntax
export const useStocksStore = defineStore("stocks", () => {
	// State: Define reactive state using ref or reactive
	const symbols_list = shallowRef<any[]>([]);
	const lastUpdated = ref<number | null>(null);
	const isLoading = ref(false);
	const error = ref<Error | null>(null);
	const most_active_stocks = shallowRef<{ [key: string]: StockItem }>({});
	const top_gainers_stocks = shallowRef<StockItem[]>([]);
	const top_gainers_loaded = ref(false);
	const top_losers_stocks = shallowRef<StockItem[]>([]);
	const stocks = shallowRef<ReferencedStocksInfo>();
	const pollingInterval = ref<NodeJS.Timeout | null>(null);
	// Timestamp (ms) until which stock-data polling should be paused after a 429 response
	const rateLimitedUntil = ref<number>(0);

	const stock_subscribed_symbols = ref(new Set<string>());
	const stock_subscriptions = reactive(new Map<string, StockItem>());

	// Constants can be defined within the setup scope
	const CACHE_DURATION_MS = 1000 * 60 * 60; // 1 hour

	// Getters: Define computed properties
	const isDataStale = computed(() => {
		// Access state refs using .value
		return (
			lastUpdated.value === null ||
			Date.now() - lastUpdated.value > CACHE_DURATION_MS
		);
	});

	// Actions: Define functions directly inside the setup function
	// Define refresh first as init calls it
	const refresh = async () => {
		isLoading.value = true; // Access state refs with .value
		error.value = null; // Access state refs with .value

		try {
			const { songbirdProxy, backend_url } = useRuntimeConfig().public;
			// const symbolsListUrl = songbirdProxy
			// 	? `${songbirdProxy}symbols-list`
			// 	: "/api/songbird/symbols-list";
			const symbolsListUrl = backend_url + "api/symbols-list";
			const res = await axios.get(symbolsListUrl);
			const fetchedData = res.data; // Use a different variable name to avoid confusion

			const lastUpdatedTimestamp = Date.now();
			// Update state refs using .value
			symbols_list.value = fetchedData;
			lastUpdated.value = lastUpdatedTimestamp;

			// Update local cache
			await stockCache.setItem("symbols", fetchedData); // Cache the data from the ref
			await stockCache.setItem("symbols_last_updated", lastUpdatedTimestamp); // Cache the timestamp from the ref
		} catch (err) {
			error.value = err as Error; // Access state refs with .value
			console.error("Error during stock symbols refresh:", err.response.data);
		} finally {
			isLoading.value = false; // Access state refs with .value
		}
	};

	const init = async () => {
		// Prevent multiple initializations if already loading
		if (isLoading.value) {
			// Access state refs with .value
			return;
		}

		isLoading.value = true; // Access state refs with .value
		error.value = null; // Access state refs with .value

		try {
			const cached = await stockCache.getItem<any[]>("symbols");
			const timestamp = await stockCache.getItem<number>("symbols_last_updated");

			// Check if cached data exists AND the timestamp is not null AND the cache is still valid
			if (
				cached &&
				timestamp !== null &&
				Date.now() - timestamp < CACHE_DURATION_MS
			) {
				symbols_list.value = cached; // Update state ref from cache
				lastUpdated.value = timestamp; // Update state ref from cache timestamp
			} else {
				await refresh(); // Call the refresh action (defined above)
			}
		} catch (err) {
			error.value = err as Error; // Access state refs with .value
			console.error("Error during stock symbols init:", err);
		} finally {
			isLoading.value = false; // Access state refs with .value
		}
	};

	function setMostActiveStocks(
		stocks: { [key: string]: StockItem } | StockItem[]
	) {
		// Convert array to object if needed (for backward compatibility)
		const stocksObject = Array.isArray(stocks)
			? stocks.reduce(
					(acc, stock) => {
						acc[stock.SYMBOL] = stock;
						return acc;
					},
					{} as { [key: string]: StockItem }
				)
			: stocks;

		// Only update if data has changed
		const currentStocks = most_active_stocks.value;
		const hasChanged =
			Object.keys(stocksObject).some((symbol) => {
				const currentStock = currentStocks[symbol];
				const newStock = stocksObject[symbol];

				if (!currentStock) {
					return true; // New stock
				}

				// Compare key properties that indicate real changes
				return (
					currentStock.BID !== newStock.BID ||
					currentStock.PC_CHG !== newStock.PC_CHG ||
					currentStock.NET_CHG !== newStock.NET_CHG ||
					currentStock.NAME !== newStock.NAME
				);
			}) || Object.keys(currentStocks).length !== Object.keys(stocksObject).length;

		if (hasChanged) {
			most_active_stocks.value = stocksObject;
		}
	}

	function updateMostActiveStocksCurrentData(
		currentDataStocks: { [key: string]: StockItem } | StockItem[]
	) {
		// Convert array to object if needed (for backward compatibility)
		const currentDataObject = Array.isArray(currentDataStocks)
			? currentDataStocks.reduce(
					(acc, stock) => {
						acc[stock.SYMBOL] = stock;
						return acc;
					},
					{} as { [key: string]: StockItem }
				)
			: currentDataStocks;

		// Get existing stocks with historic data
		const existingStocks = most_active_stocks.value;

		// Only update if current data has changed
		const hasChanged = Object.keys(currentDataObject).some((symbol) => {
			const existingStock = existingStocks[symbol];
			const newCurrentData = currentDataObject[symbol];

			if (!existingStock) {
				return true; // New stock
			}

			// Compare only current data properties (not historic data)
			return (
				existingStock.BID !== newCurrentData.BID ||
				existingStock.PC_CHG !== newCurrentData.PC_CHG ||
				existingStock.NET_CHG !== newCurrentData.NET_CHG ||
				existingStock.NAME !== newCurrentData.NAME
			);
		});

		if (hasChanged) {
			// Update stocks by preserving historic data (prices, labels) and updating current data
			const updatedStocks = { ...existingStocks };

			Object.keys(currentDataObject).forEach((symbol) => {
				const existingStock = updatedStocks[symbol];
				const newCurrentData = currentDataObject[symbol];

				// Preserve historic data if it exists, update current data
				updatedStocks[symbol] = {
					...newCurrentData, // New current data
					prices: existingStock?.prices || [], // Preserve existing prices
					labels: existingStock?.labels || [] // Preserve existing labels
				};
			});

			most_active_stocks.value = updatedStocks;
		}
	}

	function setTopGainerStocks(stocks: StockItem[]) {
		top_gainers_stocks.value = [...stocks];
	}

	function setTopLoserStocks(stocks: StockItem[]) {
		top_losers_stocks.value = [...stocks];
	}

	const getMostActiveStocks = computed(() => {
		if (
			!most_active_stocks.value ||
			Object.keys(most_active_stocks.value).length === 0
		) {
			return [];
		}
		return Object.values(most_active_stocks.value).map((item) => {
			const prices = item.HISTORIC_DATA?.map((data: any) => data.close) || [];
			const labels = item.HISTORIC_DATA?.map((data: any) => data.date) || [];

			return {
				...item,
				prices,
				labels
			};
		});
	});

	const getTopGainerStocks = computed(() => {
		if (!top_gainers_stocks.value || top_gainers_stocks.value.length === 0) {
			return [];
		}
		return top_gainers_stocks.value.map((item) => {
			const prices = item.HISTORIC_DATA?.map((data: any) => data.close) || [];
			const labels = item.HISTORIC_DATA?.map((data: any) => data.date) || [];

			return {
				...item,
				prices,
				labels
			};
		});
	});

	const getTopLoserStocks = computed(() => {
		if (!top_losers_stocks.value || top_losers_stocks.value.length === 0) {
			return [];
		}
		return top_losers_stocks.value.map((item) => {
			const prices = item.HISTORIC_DATA?.map((data: any) => data.close) || [];
			const labels = item.HISTORIC_DATA?.map((data: any) => data.date) || [];

			return {
				...item,
				prices,
				labels
			};
		});
	});

	const fetchStocksData = async (
		symbols: string[],
		includeYtd: boolean = true
	) => {
		if (import.meta.server) {
			return;
		}

		// If the API previously returned 429, honour the Retry-After window before trying again
		if (Date.now() < rateLimitedUntil.value) {
			return;
		}

		let payload: MostUpToDateDataType[] = [];

		try {
			const config = await getSongbirdConfig();

			const params: Record<string, string> = {
				symbol: symbols.join(",")
			};

			if (includeYtd) {
				params.include_ytd = "true";
			}

			const res = await axios.get(`${config.apiUrl}stocks/quote`, {
				params,
				headers: { "X-API-Key": config.apiKey },
				timeout: 15000,
				// Prevent axios from throwing on non-2xx so we can inspect 429 responses
				validateStatus: () => true
			});

			if (res.status === 429) {
				// Back off for the server-specified duration (or 60 s if header is absent)
				const retryAfterSeconds = parseInt(
					(res.headers as Record<string, string>)?.["retry-after"] ?? "60",
					10
				);
				rateLimitedUntil.value = Date.now() + retryAfterSeconds * 1000;
				console.warn(
					`[StocksStore][fetchStocksData] Rate limited by API (429) — pausing stock polling for ${retryAfterSeconds}s`
				);
				return;
			}

			if (res.status === 200) {
				rateLimitedUntil.value = 0; // Clear any prior backoff on success
				payload = res.data?.data || res.data;
			}
		} catch (error) {
			console.error("[StocksStore][fetchStocksData] ERROR:", error);
			return { data: [] };
		}

		const finalPayload = payload.reduce(
			(acc: Record<string, MostUpToDateDataType>, stock: MostUpToDateDataType) => {
				const existing = stocks.value?.[stock.SYMBOL];
				acc[stock.SYMBOL] = {
					...(existing || {}),
					...stock,
					YTD_CHG: stock.YTD_CHG ?? existing?.YTD_CHG ?? 0,
					YTD_PCT: stock.YTD_PCT ?? existing?.YTD_PCT ?? 0
				};
				return acc;
			},
			{} as Record<string, MostUpToDateDataType>
		);

		stocks.value = finalPayload;
	};

	const fetchStocks = async (symbols: string[]) => {
		if (pollingInterval.value) {
			clearInterval(pollingInterval.value);
			pollingInterval.value = null;
		}

		await fetchStocksData(symbols, true);

		pollingInterval.value = setInterval(() => {
			fetchStocksData(symbols, false);
		}, 10_000);
	};

	const cleanup = () => {
		if (pollingInterval.value) {
			clearInterval(pollingInterval.value);
			pollingInterval.value = null;
		}
		stocks.value = undefined;
		stock_subscribed_symbols.value.clear();
		stock_subscriptions.clear();
	};

	// Symbol tracking methods
	const startTrackingSymbols = async (symbols: string[]) => {
		if (pollingInterval.value) {
			clearInterval(pollingInterval.value);
			pollingInterval.value = null;
		}

		// Filter out invalid symbols (company names with spaces, special chars, etc.)
		const validSymbols = symbols.filter(
			(symbol) =>
				symbol && symbol.trim() !== "" && /^[A-Za-z0-9.\-]+$/.test(symbol)
		);

		if (validSymbols.length === 0) {
			return;
		}

		await fetchStocksData(validSymbols, true);

		pollingInterval.value = setInterval(() => {
			fetchStocksData(validSymbols, false);
		}, 10_000);
	};

	const stopTrackingSymbols = () => {
		if (pollingInterval.value) {
			clearInterval(pollingInterval.value);
			pollingInterval.value = null;
		}

		stocks.value = undefined;
		stock_subscribed_symbols.value.clear();
		stock_subscriptions.clear();
	};

	// YTD data fetching method (single batched request for all symbols)
	const fetchYtdDataForStocks = async () => {
		if (!stocks.value || Object.keys(stocks.value).length === 0) {
			return;
		}

		try {
			const { songbirdProxy } = useRuntimeConfig().public;
			const symbols = Object.keys(stocks.value);

			const response = await axios.get(`${songbirdProxy}stocks/history`, {
				params: {
					symbol: symbols.join(","),
					range: "1D",
					ytd: "true"
				},
				timeout: 15000
			});

			if (response.data?.results) {
				const results = response.data.results;
				const updated = { ...stocks.value };

				symbols.forEach((symbol) => {
					const ytdData = results[symbol];
					if (ytdData && updated[symbol]) {
						updated[symbol] = {
							...updated[symbol],
							YTD_CHG: ytdData.YTD_CHG || 0,
							YTD_PCT: ytdData.YTD_PCT || 0
						};
					}
				});

				stocks.value = updated;
			}
		} catch (error) {
			console.error("[StocksStore] Error fetching YTD data:", error);
		}
	};

	// Stock subscriptions management methods
	/**
	 * Add a stock to the subscriptions map
	 * @param stockItem - The stock item to add (SYMBOL will be used as the key)
	 */
	const addStockSubscription = (stockItem: StockItem) => {
		stock_subscriptions.set(stockItem.SYMBOL, stockItem);
	};

	/**
	 * Add multiple stocks to the subscriptions map
	 * @param stockItems - Array of stock items to add
	 */
	const addMultipleStockSubscriptions = (stockItems: StockItem[]) => {
		stockItems.forEach((stockItem) => {
			stock_subscriptions.set(stockItem.SYMBOL, stockItem);
		});
	};

	/**
	 * Update a specific stock in the subscriptions map
	 * @param symbol - The stock symbol to update
	 * @param updates - Partial stock data to update
	 */
	const updateStockSubscription = (
		symbol: string,
		updates: Partial<StockItem>
	) => {
		const existingStock = stock_subscriptions.get(symbol);
		if (existingStock) {
			const updatedStock = { ...existingStock, ...updates };
			stock_subscriptions.set(symbol, updatedStock);
		} else {
			console.warn(
				`[StocksStore] Cannot update ${symbol}: not found in subscriptions`
			);
		}
	};

	/**
	 * Remove a stock subscription
	 * @param symbol - The stock symbol to remove
	 */
	const removeStockSubscription = (symbol: string) => {
		const removed = stock_subscriptions.delete(symbol);
		if (!removed) {
			console.warn(
				`[StocksStore] Cannot remove ${symbol}: not found in subscriptions`
			);
		}
		return removed;
	};

	/**
	 * Clear all stock subscriptions
	 */
	const clearStockSubscriptions = () => {
		stock_subscriptions.clear();
	};

	/**
	 * Check if a stock is subscribed
	 * @param symbol - The stock symbol to check
	 */
	const hasStockSubscription = (symbol: string): boolean => {
		return stock_subscriptions.has(symbol);
	};

	/**
	 * Get a specific stock subscription
	 * @param symbol - The stock symbol to get
	 */
	const getStockSubscription = (symbol: string): StockItem | undefined => {
		return stock_subscriptions.get(symbol);
	};

	/**
	 * Extract symbols from odd indices of stock_pairs array and save to stock_subscribed_symbols set
	 * @param stock_pairs - Array where symbols are at odd indices (1, 3, 5, etc.)
	 */
	const setSubscribedSymbolsFromPairs = (stock_pairs: string[]) => {
		// Clear existing symbols
		stock_subscribed_symbols.value.clear();

		// Extract symbols from odd indices and add to set
		stock_pairs.forEach((item, index) => {
			if (index % 2 === 1) {
				// Odd indices (1, 3, 5, etc.)
				stock_subscribed_symbols.value.add(item);
			}
		});
	};

	/**
	 * Add symbols from stock_pairs array to existing stock_subscribed_symbols set (without clearing)
	 * @param stock_pairs - Array where symbols are at odd indices (1, 3, 5, etc.)
	 */
	const addSubscribedSymbolsFromPairs = (stock_pairs: string[]) => {
		// Extract symbols from odd indices and add to existing set
		stock_pairs.forEach((item, index) => {
			if (index % 2 === 1) {
				// Odd indices (1, 3, 5, etc.)
				stock_subscribed_symbols.value.add(item);
			}
		});
	};

	// Computed getters for stock subscriptions
	const getStockSubscriptions = computed(() => {
		return Array.from(stock_subscriptions.values());
	});

	const getStockSubscriptionsMap = computed(() => {
		return stock_subscriptions;
	});

	const getStockSubscriptionsCount = computed(() => {
		return stock_subscriptions.size;
	});

	const getSubscribedSymbols = computed(() => {
		return Array.from(stock_subscriptions.keys());
	});

	// Computed getters for stock_subscribed_symbols set
	const getSubscribedSymbolsSet = computed(() => {
		return stock_subscribed_symbols.value;
	});

	const getSubscribedSymbolsArray = computed(() => {
		return Array.from(stock_subscribed_symbols.value);
	});

	const getSubscribedSymbolsCount = computed(() => {
		return stock_subscribed_symbols.value.size;
	});

	// Debounced watcher: wait for symbol changes to settle before fetching quotes.
	// Prevents rapid-fire requests when multiple symbols are added in quick succession.
	let subscribedSymbolsDebounceTimer: ReturnType<typeof setTimeout> | null =
		null;

	watch(getSubscribedSymbolsArray, (newVal, oldVal) => {
		if (!import.meta.client || newVal === oldVal) return;

		// Clear any pending debounce
		if (subscribedSymbolsDebounceTimer) {
			clearTimeout(subscribedSymbolsDebounceTimer);
		}

		subscribedSymbolsDebounceTimer = setTimeout(async () => {
			// Filter out symbols containing special characters, spaces, or empty strings
			const validSymbols = newVal.filter(
				(symbol) =>
					symbol &&
					symbol.trim() !== "" &&
					!/[.+\s]/.test(symbol) &&
					/^[A-Za-z0-9-]+$/.test(symbol)
			);

			if (validSymbols.length === 0) {
				return;
			}

			try {
				const config = await getSongbirdConfig();
				const { data: stock_response, status } = await axios.get(
					`${config.apiUrl}stocks/quote`,
					{
						params: {
							symbol: validSymbols.join(","),
							include_ytd: "true"
						},
						headers: { "X-API-Key": config.apiKey },
						timeout: 15000
					}
				);

				if (status === 200) {
					addMultipleStockSubscriptions(stock_response.data);
				}
			} catch (error) {
				console.warn(
					"[StocksStore] Failed to fetch subscribed symbols quote:",
					(error as any)?.message || error
				);
			}
		}, 300); // 300ms debounce
	});

	// Return everything you want to expose from the store
	return {
		// State (exposed as refs)
		stocks,
		symbols_list,
		lastUpdated,
		isLoading,
		error,
		stock_subscribed_symbols,
		// Getters (exposed as computed refs)
		isDataStale,
		// Actions (exposed as functions)
		init,
		refresh,
		most_active_stocks,
		setMostActiveStocks,
		updateMostActiveStocksCurrentData,
		getMostActiveStocks,
		top_gainers_stocks,
		top_gainers_loaded,
		setTopGainerStocks,
		getTopGainerStocks,
		top_losers_stocks,
		setTopLoserStocks,
		getTopLoserStocks,
		fetchStocks,
		cleanup,
		fetchStocksData,
		startTrackingSymbols,
		stopTrackingSymbols,
		fetchYtdDataForStocks,
		// Stock subscriptions methods
		addStockSubscription,
		addMultipleStockSubscriptions,
		updateStockSubscription,
		removeStockSubscription,
		clearStockSubscriptions,
		hasStockSubscription,
		getStockSubscription,
		// Stock pairs methods
		setSubscribedSymbolsFromPairs,
		addSubscribedSymbolsFromPairs,
		// Stock subscriptions getters
		getStockSubscriptions,
		getStockSubscriptionsMap,
		getStockSubscriptionsCount,
		getSubscribedSymbols,
		// Stock subscribed symbols getters
		getSubscribedSymbolsSet,
		getSubscribedSymbolsArray,
		getSubscribedSymbolsCount,
		stock_subscriptions
	};
});
